//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import { useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
// import { browserName } from 'react-device-detect';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import useSimpleBarRef from '../common/hooks/useSimpleBarRef';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import homeBanner1 from '../images/Banner/Home_V1.jpg';
import homeBanner2 from '../images/Banner/Home_V2.jpg';
import homeBanner3 from '../images/Banner/Home_V3.jpg';
import homeBanner4 from '../images/Banner/Home_V4.jpg';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdCompanyName from '../common/elemental/AfitdCompanyName';
import AfitdTypography from '../common/elemental/AfitdTypography';
import AfitdButton from '../common/elemental/AfitdButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import ExternalVirtualInterviewDialog from '../empDashboardMyCalendar/subComponents/ExternalVirtualInterviewDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import { format, differenceInMinutes } from 'date-fns';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = params => {
    const { currentBanner } = params;
    return makeStyles(theme => ({
        root: {},
        pageContainer: {
            backgroundColor: '#FAFAFC',
            overflow: 'hidden',
            height: '100%',
            minHeight: '100%',
        },
        pageHeader: {
            maxHeight: '81px',
            // [theme.breakpoints.down('md')]: { maxHeight: '134px' }, // commented out because no more second row menu -el 20221022
            // [theme.breakpoints.down('sm')]: { maxHeight: '81px' },
        },
        pageContent: {
            padding: 20,
            maxWidth: '580px',
            margin: 'auto',
            marginTop: '0px',
        },
        tempContainer: { alignItems: 'center', marginTop: 50 },
        pagePicture: {
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),url('${currentBanner}')`,
            height: '780px',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            position: 'relative',
            width: '100%',
            // animation: `$backgroundBannerTranistion 1000ms ${theme.transitions.easing.easeInOut}`,
            [theme.breakpoints.down('md')]: { height: '540px' },
        },
        bannerTextContainer: {
            padding: '30px',
            marginTop: '60px',
            [theme.breakpoints.down('md')]: { marginTop: '50px', width: '500px' },
            [theme.breakpoints.down('sm')]: { marginTop: '50px', width: '100%' },
            // maxWidth: '600px',
        },
        bannerHeading: {
            fontSize: 50,
            color: 'white',
            paddingTop: 60,
            // paddingBottom: 124,
            // [theme.breakpoints.down('lg')]: { fontSize: 50,  }, // paddingBottom: 100
            // [theme.breakpoints.up('lg')]: { fontSize: 65,  }, // paddingBottom: 100
            [theme.breakpoints.down('md')]: { fontSize: 38, paddingTop: 0 }, // paddingBottom: 100
            [theme.breakpoints.down('sm')]: { fontSize: 25, paddingTop: 0 }, // paddingBottom: 130
        },
        bannerParagraph: {
            fontSize: 20,
            [theme.breakpoints.down('md')]: { fontSize: 16 },
            color: 'rgba(255, 255, 255, 0.8)',
            // paddingBottom: 40,
        },
        employerBrandPictureContainer: {
            textAlign: 'center',
            marginTop: '30px',
            [theme.breakpoints.down('md')]: { marginTop: '0px' },
        },
        employerBrandPicture: {
            width: '90px',
            height: '90px',
            borderRadius: '10px',
            border: '1px solid #F0F2F6',
        },
        companyName: {
            justifyContent: 'center',
            '& > p': {
                fontSize: '25px',
            },
        },
        joinCodeContainer: {
            // textAlign: 'center',
            marginTop: '20px',
            marginBottom: '20px',
            // display: 'flex',
            // alignItems: 'center',
            // justifyContent: 'space-between',
        },
        currentDate: {
            textAlign: 'left',
            fontSize: '16px',
            color: 'rgba(17, 30, 20, 0.6)',
            paddingTop: '10px',
        },
        calenderIcon: {
            fontSize: '14px',
            marginRight: '10px',
        },
        interviewDetailBlock: {
            padding: '30px',
            background: '#FFFFFF',
            border: '1px solid #D5D7DD',
            boxShadow: '0px 14.96px 20px rgba(0, 0, 0, 0.07)',
            borderRadius: '8px',
            // dt: new Date(),
            display: 'flex',
        },
        interviewStatusIcon: {
            fontSize: 44,
            backgroundColor: 'white',
            color: '#FC821F',
            [theme.breakpoints.down('sm')]: { fontSize: 26 },
            maxWidth: '480px',
        },
        interviewDetails: {
            marginLeft: '10px',
            marginTop: '5px',
            [theme.breakpoints.down('sm')]: { marginTop: '0px' },
        },
        interviewHeading: {
            color: '#111E14',
            fontWeight: 'bold',
            fontSize: '20px',
        },
        jobTitle: {
            color: '#111E14',
            fontSize: '14px',
        },
        interviewers: {
            color: '#111E14',
            fontSize: '14px',
        },
        interviewTime: {
            color: '#111E14',
            fontWeight: 'bold',
            fontSize: '16px',
            paddingTop: '10px',
        },
        interviewDate: {
            color: '#111E14',
            fontSize: '16px',
        },
        enterSessionButtonDiv: {
            textAlign: 'center',
            marginBottom: '30px',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        token,
        companyName,
        isEmpBadge,
        interviewId,
        jobTitle,
        participantName,
        enterSessionButtonDisabled,
        handleClickEnterSessionButton,
        isJoinVirtualInterview,
        handleCloseVirtualInterview,
        joinRequestUserType,
    } = params;

    const defns = {
        companyName: { companyName, isEmpBadge },

        pageContainer: { container: true, alignItems: 'center' },
        pagePicture: { item: true, lg: 6, md: 12, sm: 12, xs: 12 },
        pageContent: { item: true, lg: 6, md: 12, sm: 12, xs: 12 },
        interviewDetailBlock: {},
        enterSessionButton: {
            color: 'secondary',
            onClick: handleClickEnterSessionButton,
            disabled: enterSessionButtonDisabled,
        },
        virtualInterview: {
            open: isJoinVirtualInterview,
            token,
            interviewId,
            jobTitle,
            companyName,
            joinRequestUserType,
            userName: participantName,
            onClose: handleCloseVirtualInterview,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const backgroundImages = [homeBanner1, homeBanner2, homeBanner3, homeBanner4];

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const InterviewDetails = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const theme = useTheme();
    const lgMatchesUp = useMediaQuery(theme.breakpoints.up('lg'));
    const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    // const appContext = useContext(AppContext);
    // const { authUser } = appContext;
    const context = useContext(LayoutContext);
    // const { setLoadingDialogOpen } = context;
    const { scrollToTop: scrollToPageTop } = useSimpleBarRef();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { data, token, autoLaunch } = props;
    const interviewId = _get(data, 'uid');
    const companyName = _get(data, 'companyName');
    const isEmpBadge = _get(data, 'isEmpBadge');
    const jobTitle = _get(data, 'jobTitle');
    const interviewers = _get(data, 'interviewers');
    const interviewersNames = interviewers?.map(el => el.name);
    const participantName = _get(data, 'participantName');
    const jobSeekerName = _get(data, 'jobSeekerName');
    const companyProfilePicUrl = _get(data, 'companyProfilePicUrl');
    const joinRequestUserType = _get(data, 'joinRequestUserType');
    const startDt = _get(data, 'startDt');
    const endDt = _get(data, 'endDt');
    const interviewDate = new Date(startDt).toLocaleDateString();
    const currentDate = new Date(Date.now()).toLocaleDateString();
    const interviewTimeText = startDt ? `${format(startDt, 'p')} - ${format(endDt, 'p')}` : '';
    const minutesLeft = differenceInMinutes(new Date(startDt), new Date(Date.now()));
    const interviewDay = new Date(startDt).getDay();

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [currentBanner] = useState(backgroundImages[Math.floor(Math.random() * 4)]);
    const [counter, setCounter] = useState(0);
    const [enterSessionButtonDisabled, setEnterSessionButtonDisabled] = useState(true);
    const [isJoinVirtualInterview, setIsJoinVirtualInterview] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        if (autoLaunch && data) setIsJoinVirtualInterview(true);
    }, [autoLaunch, data]);

    useEffect(() => {
        if (minutesLeft < 60) {
            // alert(browserName);
            if (minutesLeft > 2) {
                setEnterSessionButtonDisabled(true);
                setTimeout(() => {
                    setCounter(counter + 1);
                }, 1000);
            } else setEnterSessionButtonDisabled(false);
        }
    }, [counter, minutesLeft]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickEnterSessionButton = () => {
        // open virtual interview modal
        setIsJoinVirtualInterview(true);
    };

    const handleCloseVirtualInterview = () => {
        setIsJoinVirtualInterview(false);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ currentBanner })();
    const defns = getDefns({
        classes,
        props,
        token,
        companyName,
        isEmpBadge,
        interviewId,
        jobTitle,
        participantName,
        enterSessionButtonDisabled,
        handleClickEnterSessionButton,
        isJoinVirtualInterview,
        handleCloseVirtualInterview,
        joinRequestUserType,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider
            value={{
                ...context,
                scrollToPageTop,
            }}
        >
            <Grid {...defns.pageContainer}>
                {lgMatchesUp && (
                    <Grid {...defns.pagePicture}>
                        <div {...defns.bannerTextContainer}>
                            <h1 {...defns.bannerHeading}>
                                Show The World What You’ve Got.
                                <br />
                                Find Jobs That Fit Your Story.
                            </h1>
                            <p {...defns.bannerParagraph}>
                                <span style={{ fontWeight: 'bold' }}> A Foot in the Door</span> is a next-generation job search platform dedicated exclusively to job seekers with
                                0-5 years of work experience.{lgMatchesUp && <br />}
                            </p>
                        </div>
                    </Grid>
                )}
                <Grid {...defns.pageContent}>
                    <div {...defns.employerBrandPictureContainer}>
                        <img src={companyProfilePicUrl} alt="" {...defns.employerBrandPicture} />
                        <br />
                        <br />
                        <AfitdCompanyName {...defns.companyName} />
                    </div>
                    <div {...defns.joinCodeContainer}>
                        <AfitdTypography variant="h6" color="primary">
                            {companyName} - {jobTitle}
                        </AfitdTypography>
                        <AfitdTypography {...defns.currentDate}>
                            <CalendarTodayIcon {...defns.calenderIcon} />
                            {currentDate}
                        </AfitdTypography>
                    </div>
                    <div {...defns.interviewDetailBlock}>
                        <div>
                            <CheckCircleIcon {...defns.interviewStatusIcon} />
                        </div>
                        <div {...defns.interviewDetails}>
                            <AfitdTypography {...defns.interviewHeading}> Interview with {jobSeekerName}</AfitdTypography>
                            {interviewersNames && <AfitdTypography {...defns.interviewers}> w/: {interviewersNames?.join(', ')}</AfitdTypography>}
                            <br />
                            <AfitdTypography {...defns.interviewDate}>
                                {' '}
                                {weekday[interviewDay]}, {interviewDate}{' '}
                            </AfitdTypography>
                            <AfitdTypography {...defns.interviewTime}> {interviewTimeText}</AfitdTypography>
                        </div>
                    </div>
                    <br />
                    <div {...defns.enterSessionButtonDiv}>
                        <AfitdButton {...defns.enterSessionButton}>Enter Session as {participantName}</AfitdButton>
                    </div>
                    <div>
                        <br />
                        <AfitdTypography align="center">* The interview session will open 3 minutes prior to the start time</AfitdTypography>
                    </div>
                </Grid>
                {!lgMatchesUp && (
                    <Grid {...defns.pagePicture}>
                        <div {...defns.bannerTextContainer}>
                            <h1 {...defns.bannerHeading}>
                                Show The World What You’ve Got.
                                <br />
                                Find Jobs That Fit Your Story.
                            </h1>
                            <p {...defns.bannerParagraph}>
                                <span style={{ fontWeight: 'bold' }}> A Foot in the Door</span> is a job search platform dedicated exclusively to job {lgMatchesUp && <br />}
                                seekers with 0-5 years of work experience.
                            </p>
                        </div>
                    </Grid>
                )}
            </Grid>
            <ExternalVirtualInterviewDialog {...defns.virtualInterview} />
        </LayoutContext.Provider>
    );
};

export default InterviewDetails;
